import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { WorkflowEventTypes } from '@amzn/awscat-pattern-library-service-model';

import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { evaluateEditEligibility } from '../../model/EditEligibility';
import { useCoAuthors } from '../v2-hooks/useCoAuthors';
import { useLoadedContentId } from '../v2-hooks/useLoadedContentId';
import { useWorkflow } from '../v2-hooks/useWorkflow';
import { log } from '../../loggers/Logger';

export interface ViewModeStatePanelViewModel {
  /** Whether the current user's active role is allowed to edit the content. */
  readonly canEdit: boolean;
  /** Whether the Edit button is disabled because the user may not edit the content right now. */
  readonly isEditDisabled: boolean;
  /** True while the lock is being acquired and the edit page is loading. */
  readonly isEditInProgress: boolean;
  /** Display-ready message when acquiring the lock failed; `undefined` otherwise. */
  readonly editError: string | undefined;
  /** Acquire the edit lock for the loaded content, then navigate to the edit page. */
  readonly editContent: () => void;
}

/**
 * View model for the read-only view-page state panel. Exposes whether the
 * active role may edit and an action to navigate to the edit page.
 */
export function useViewModeStatePanelViewModel(): ViewModeStatePanelViewModel {
  const { userId, activeRole } = useCurrentUser();
  const service = useContentEditorService();
  const contentId = useLoadedContentId();
  const workflow = useWorkflow();
  const coAuthors = useCoAuthors();
  const history = useHistory();

  // Publishers never see the Edit affordance; mirrors PublicationStatePanel.
  const canEdit = activeRole !== 'bar_raiser';

  // Base eligibility is the rule shared with the direct /edit route guard.
  const eligibility = evaluateEditEligibility({ workflow, userId, activeRole, coAuthors });

  // The shared rule allows editing under a system lock (an author may edit
  // during a QC scan), but the viewer's Edit button must stay disabled while
  // any other holder — including the system — holds the lock, so clicking it
  // can't fire a LOCK event that conflicts. Admins are not lock-gated.
  const isLockedByAnotherHolder =
    activeRole !== 'admin' && !!workflow?.isLocked && workflow.lock?.acquiredBy !== userId;

  const isEditDisabled = !eligibility.canEdit || isLockedByAnotherHolder;

  const [isEditInProgress, setIsEditInProgress] = useState(false);
  const [editError, setEditError] = useState<string | undefined>(undefined);

  const editContent = useCallback(async () => {
    if (!contentId || !workflow) return;

    setIsEditInProgress(true);
    setEditError(undefined);

    // Acquire the edit lock for this author unless it's already held by them
    // (re-entering edit). The LOCK workflow event is the same mechanism the
    // legacy author flow uses; it polls until the server applies the lock.
    if (!(workflow.isLocked && workflow.lock?.acquiredBy === userId)) {
      try {
        await createWorkflowEventWithStatePolling(
          contentId,
          workflow.draftWorkflowId,
          WorkflowEventTypes.LOCK,
          '',
          undefined,
          undefined,
          undefined,
          { suppressSuccessFlashbar: true },
        );
      } catch (err) {
        log.error({ err, contentId }, 'Failed to acquire edit lock');
        setEditError('Could not lock this content for editing. Please try again.');
        setIsEditInProgress(false);
        return;
      }
    }

    // Close the open document before navigating so the edit page can re-open
    // it (the service rejects open() while content is already open), and so
    // the teardown's 'changed' emission lands before this page unmounts rather
    // than after — avoiding a post-unmount setState in the panel's hooks.
    try {
      await service.closeAndDiscard();
    } catch (err) {
      log.warn({ error: err, contentId }, 'Failed to close content before navigating to edit');
    }
    history.push(`/edit/${contentId}`);
  }, [service, history, contentId, workflow, userId]);

  return {
    canEdit,
    isEditDisabled,
    isEditInProgress,
    editError,
    editContent,
  };
}
